import { useEffect, useState } from 'react';

interface StickyBarInfo {
  height: number;
  isVisible: boolean;
}

interface UseStickyBarHeightOptions {
  selectors?: string[];
  throttleMs?: number;
  isMinimized?: boolean;
  isMobile?: boolean;
}

const DEFAULT_SELECTORS = ['[data-testid="lead-sticky-banner"]'];

const useStickyBarHeight = ({
  selectors = DEFAULT_SELECTORS,
  throttleMs = 100,
  isMinimized = false,
  isMobile = false,
}: UseStickyBarHeightOptions = {}): StickyBarInfo => {
  const [stickyBarInfo, setStickyBarInfo] = useState<StickyBarInfo>({
    height: 0,
    isVisible: false,
  });
  const [debouncedInfo, setDebouncedInfo] = useState<StickyBarInfo>({
    height: 0,
    isVisible: false,
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const calculateStickyBarHeight = () => {
      let totalHeight = 0;
      let hasVisibleStickyElements = false;

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);

        elements.forEach((element) => {
          const htmlElement = element as HTMLElement;
          const rect = htmlElement.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(htmlElement);

          const isVisible =
            rect.height > 0 &&
            computedStyle.display !== 'none' &&
            computedStyle.visibility !== 'hidden';

          const isStickyOrFixed =
            computedStyle.position === 'sticky' || computedStyle.position === 'fixed';

          if (isVisible && isStickyOrFixed) {
            if (computedStyle.position === 'fixed') {
              const isAtBottom = rect.bottom >= window.innerHeight - 10;
              if (isAtBottom) {
                totalHeight += rect.height;
                hasVisibleStickyElements = true;
              }
            } else if (computedStyle.position === 'sticky') {
              const isStickyAtBottom =
                computedStyle.bottom === '0px' ||
                computedStyle.bottom === '0' ||
                htmlElement.classList.contains('bottom-0') ||
                htmlElement.getAttribute('class')?.includes('bottom-0');

              if (isStickyAtBottom) {
                totalHeight += rect.height;
                hasVisibleStickyElements = true;
              }
            }
          }
        });
      });

      setStickyBarInfo({
        height: totalHeight,
        isVisible: hasVisibleStickyElements,
      });
    };

    const mobileThrottleMs = isMobile ? Math.max(throttleMs * 3, 300) : throttleMs;

    const throttledCalculate = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(calculateStickyBarHeight, mobileThrottleMs);
    };

    calculateStickyBarHeight();

    window.addEventListener('scroll', throttledCalculate, { passive: true });
    window.addEventListener('resize', throttledCalculate, { passive: true });

    const observer = new MutationObserver(throttledCalculate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      window.removeEventListener('scroll', throttledCalculate);
      window.removeEventListener('resize', throttledCalculate);
      observer.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectors, throttleMs, isMinimized, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setDebouncedInfo(stickyBarInfo);
      return;
    }

    const debounceTimeout = setTimeout(() => {
      setDebouncedInfo(stickyBarInfo);
    }, 150);

    return () => clearTimeout(debounceTimeout);
  }, [stickyBarInfo, isMobile]);

  return isMobile ? debouncedInfo : stickyBarInfo;
};

export default useStickyBarHeight;
